import React from 'react';
import { useTenantConfig } from '../../../hooks';
import { getTrackingData } from '@teko/tracking';
import { useFeatureFlags, useLocation } from 'contexts';
import { RegionPickerSuggestionPopover } from 'components/RegionPickerSuggestionPopover';
import { Caption, styled } from '@teko/ds-mobile';
import { isServerless } from 'helpers';
import {
  LocationOutlined,
  FacebookMessengerFilled,
  ZaloFilled,
} from '@teko/ds-icon';
import { CartHeader } from './CartHeader';

interface HeaderIconProps {
  isVisible?: boolean;
  showRegionPopup?: boolean;
  setShowRegionPopup: (showRegionPopup: boolean) => void;
  transitionDelay?: string;
  isDisplayContact?: boolean;
  onlyShowLocationIcon?: boolean;
  isShowCartIcon?: boolean;
}

const Wrapper = styled.div<Omit<HeaderIconProps, 'setShowRegionPopup'>>(
  ({ isVisible, transitionDelay, theme }) => `
    max-width: ${isVisible ? '180px' : '0px'};
    display: ${isVisible ? 'flex' : 'none'};
    transition: max-width 0.2s ${isVisible ? 'ease-in' : 'ease-out'};
    transition-delay: ${isVisible ? transitionDelay : '0s'};
    overflow: hidden;
    align-items: center;
    column-gap: ${theme.spacing.s16};
    flex: 0 0 auto;
  `,
);

const Anchor = styled.a``;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeaderIcon: React.FC<HeaderIconProps> = ({
  isVisible = true,
  showRegionPopup,
  setShowRegionPopup,
  transitionDelay = '0s',
  isDisplayContact = true,
  onlyShowLocationIcon,
  isShowCartIcon = false,
}: HeaderIconProps) => {
  const { contactLink, isDisplayMessenger, zaloContactLink, originalTerminal } =
    useTenantConfig();
  const {
    locations,
    selectedLocation,
    showRegionPickerSuggestionPopover,
    onCloseRegionPickerSuggestionPopover,
  } = useLocation();
  const { flags } = useFeatureFlags();
  const isShowRegionPickerIcon =
    isServerless() && flags.epic_ecom_284
      ? locations.length > 1
      : Array.isArray(originalTerminal) && originalTerminal?.length > 1;

  const regionIcon = (
    <IconWrapper onClick={(): void => setShowRegionPopup(!showRegionPopup)}>
      <LocationOutlined size={30} color="white" />
      {flags.epic_ecom_284 && !!selectedLocation && !onlyShowLocationIcon && (
        <Caption level={2} color="white" line={1}>
          {selectedLocation.locationName}
        </Caption>
      )}
    </IconWrapper>
  );

  const regionPopper = (
    <RegionPickerSuggestionPopover
      isMobile={true}
      active={showRegionPickerSuggestionPopover}
      onClose={onCloseRegionPickerSuggestionPopover}
    >
      {regionIcon}
    </RegionPickerSuggestionPopover>
  );

  return (
    <>
      <Wrapper isVisible={isVisible} transitionDelay={transitionDelay}>
        {isDisplayContact && contactLink && isDisplayMessenger && (
          <Anchor href={contactLink} target="_blank" rel="noreferrer">
            <IconWrapper
              {...getTrackingData({
                isTrackContent: true,
                regionName: 'quickAction',
                contentName: 'fbMessengerIcon',
              })}
            >
              <FacebookMessengerFilled size={30} color="#fff" />
            </IconWrapper>
          </Anchor>
        )}
        {zaloContactLink && (
          <Anchor href={zaloContactLink} target="_blank" rel="noreferrer">
            <IconWrapper
              {...getTrackingData({
                isTrackContent: true,
                regionName: 'quickAction',
                contentName: 'zaloMessengerIcon',
              })}
            >
              <ZaloFilled size={28} color="#fff" />
            </IconWrapper>
          </Anchor>
        )}
        {isShowRegionPickerIcon
          ? !onlyShowLocationIcon
            ? regionPopper
            : regionIcon
          : null}
        {isShowCartIcon && (
          <CartIcon
            iconSize={24}
            badgeWidth={12}
            badgeTop={-2}
            iconColor="#fff"
            type="filled"
          />
        )}
      </Wrapper>
    </>
  );
};

const CartIcon = styled(CartHeader)`
  padding: 0;
  .cart-badge {
    background-color: white;
    color: black;
    line-height: 12px;
    height: 12px;
  }
`;
